import { AskCustomerButton, CustomerRequestSeed } from '../../../../customerRequests/CustomerRequestComposer';
import React, { useEffect, useRef } from "react";
import { Link2 } from "lucide-react";
import { primaryText, secondaryText, greyTextColor, textBlue, borderPrimary, greenTextColor } from "../../../../../styling/stylingHelper";
import { eur } from "../../../../orderhistory/utils";
import { SupplierLogo } from "../../../../ui/SupplierLogo";
import { getIngredientUnitPrice, getProductPlatformDisplay } from "./useCostPriceDetail";
import { isPracticeId } from "../../../../onboarding/practice/practiceGuards";
import { usePracticeScope } from "../../../../onboarding/practice/practiceScope";
import { productKey } from "../../../../../utils/reports/shared";
import SourceRestaurantBadge from "./SourceRestaurantBadge";
import { OverrideTag, OverrideResetButton } from "./ChoiceOverrideControls";
import { applyWaste, resolveWaste } from "../../../../../utils/wasteUtils";
import { useTranslation } from "react-i18next";
import type { CostPriceIngredient } from "../../../../../services/costPrice/costPriceService";
import type { SubrecipeIngredient } from "../../../../../services/subrecipes/subrecipesService";

const DeleteIcon = () => (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M2.5 5H4.16667H17.5" stroke="#F04438" strokeWidth="1.67" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M6.66699 5V3.33333C6.66699 2.89131 6.84259 2.46738 7.15515 2.15482C7.46771 1.84226 7.89164 1.66667 8.33366 1.66667H11.667C12.109 1.66667 12.5329 1.84226 12.8455 2.15482C13.158 2.46738 13.3337 2.89131 13.3337 3.33333V5M15.8337 5V16.6667C15.8337 17.1087 15.6581 17.5326 15.3455 17.8452C15.033 18.1577 14.609 18.3333 14.167 18.3333H5.83366C5.39164 18.3333 4.96771 18.1577 4.65515 17.8452C4.34259 17.5326 4.16699 17.1087 4.16699 16.6667V5H15.8337Z" stroke="#F04438" strokeWidth="1.67" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M8.33301 9.16667V14.1667" stroke="#F04438" strokeWidth="1.67" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M11.667 9.16667V14.1667" stroke="#F04438" strokeWidth="1.67" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
);

interface IngredientsSectionProps {
    requestContext?: CustomerRequestSeed;
    requestDisabled?: boolean;
    ingredients: any[];
    inlineQuantities: Record<string, string>;
    confirmingDeleteId: string | null;
    onQuantityChange: (id: string, value: string) => void;
    onSaveEdit: (id: string, qty: string, originalQty: number) => void;
    onDelete: (ingredient: any) => void;
    onConfirmDelete: (ingredient: any) => void;
    onClearAll?: () => void;
    headerActions?: React.ReactNode;
    // Restaurant-wide default waste %, shown as the placeholder when a row has no
    // explicit override and folded into the displayed row cost.
    defaultWastePercentage?: number;
    inlineWaste?: Record<string, string>;
    onWasteChange?: (id: string, value: string) => void;
    onSaveWaste?: (id: string, value: string) => void;
    // BYO dish context: row ids whose quantity is a per-dish override, with a
    // reset action back to the shared quantity. Absent outside dish context.
    overriddenKeys?: Set<string>;
    onResetOverride?: (id: string) => void;
    // Opens the Alternatives editor for an order-history ingredient row. When
    // omitted, no alternatives affordance is shown. Alternatives are only valid on
    // real order-history rows (never custom / subrecipe-ref / nested rows).
    onOpenAlternatives?: (ingredient: CostPriceIngredient | SubrecipeIngredient) => void;
    // Opens the link picker for a CUSTOM ingredient row. When omitted, no link
    // affordance is shown. Custom rows are cost-price placeholders — linking
    // re-points them at real purchase data.
    onLinkCustomIngredient?: (ingredient: CostPriceIngredient | SubrecipeIngredient) => void;
    // On mount, scroll to the order-history row whose resolver key (productKey)
    // matches this and focus its quantity input. No-op when absent / not found.
    focusProductKey?: string;
}

const IngredientsSection: React.FC<IngredientsSectionProps> = ({
    requestContext, requestDisabled, ingredients, inlineQuantities, confirmingDeleteId,
    onQuantityChange, onSaveEdit, onDelete, onConfirmDelete, onClearAll, headerActions,
    defaultWastePercentage = 0, inlineWaste, onWasteChange, onSaveWaste,
    overriddenKeys, onResetOverride, onOpenAlternatives, onLinkCustomIngredient, focusProductKey
}) => {
    const { t } = useTranslation();
    const practiceScope = usePracticeScope();
    // One-shot focus of the requested row's quantity input (setup flow lands the
    // user on the just-added ingredient). Attached during render to the matching
    // input; the effect runs once the row exists.
    const focusInputRef = useRef<HTMLInputElement>(null);
    const didFocusRef = useRef(false);
    useEffect(() => {
        if (!focusProductKey || didFocusRef.current) return;
        const input = focusInputRef.current;
        if (!input) return;
        didFocusRef.current = true;
        input.scrollIntoView({ block: 'center' });
        input.focus();
        input.select();
    }, [focusProductKey, ingredients]);

    useEffect(() => {
        if (!practiceScope) return;
        const fill = (event: Event) => {
            const { name, qty } = (event as CustomEvent).detail ?? {};
            const row = ingredients.find(i => isPracticeId(i.articleId) && i.productName === name);
            if (!row || !Number.isFinite(Number(qty)) || Number(qty) < 0) return;
            onQuantityChange(row.id, String(qty));
            if (Number(qty) !== row.quantityNeeded) onSaveEdit(row.id, String(qty), row.quantityNeeded);
        };
        window.addEventListener('onboarding:practice-ingredient-quantity', fill);
        return () => window.removeEventListener('onboarding:practice-ingredient-quantity', fill);
    }, [practiceScope, ingredients, onQuantityChange, onSaveEdit]);

    if (!ingredients || ingredients.length === 0) return null;

    return (
        <div className={`rounded-xl border ${borderPrimary} bg-background dark:bg-background-dark p-4 mt-5`}>
            <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                    <img src="/assets/ingredients.svg" alt="ingredients" />
                    <span className={`text-[16px] leading-5 font-semibold ${primaryText}`}>Ingredients</span>
                </div>
                <div className="flex items-center gap-1">
                    {headerActions}
                    {onClearAll && (
                        <button type="button" onClick={onClearAll} className="p-1 hover:opacity-70 transition-opacity">
                            <DeleteIcon />
                        </button>
                    )}
                </div>
            </div>

            <div className="space-y-3">
                {ingredients.map((ingredient, idx) => {
                    const unitPrice = getIngredientUnitPrice(ingredient);
                    const currentQtyStr = inlineQuantities[ingredient.id] ?? ingredient.quantityNeeded.toString();
                    const currentQty = parseFloat(currentQtyStr) || 0;
                    // Waste % — explicit override (incl. 0) wins, else inherit the restaurant default.
                    const wasteStr = inlineWaste?.[ingredient.id] ?? (ingredient.wastePercentage !== undefined ? ingredient.wastePercentage.toString() : '');
                    const resolvedWaste = resolveWaste(
                        wasteStr.trim() === '' ? undefined : (parseFloat(wasteStr) || 0),
                        defaultWastePercentage
                    );
                    const ingredientCost = ingredient.currentPrice ? applyWaste(currentQty, resolvedWaste) * ingredient.currentPrice : 0;
                    const platformDisplay = getProductPlatformDisplay(ingredient.platform, (ingredient as any).customPlatformName);
                    // Order-history rows only (never custom / subrecipe-ref / nested) — matches the alternatives domain.
                    const isFocusTarget = !!focusProductKey
                        && !ingredient.custom && !ingredient.isCustom
                        && !ingredient.subrecipeId && !ingredient.nestedCostPriceId
                        && productKey(ingredient.platform ?? '', ingredient.articleId, ingredient.productName) === focusProductKey;

                    // Footer strip: the two affordances are mutually exclusive — alternatives
                    // are stripped from custom rows on write, so a custom row never has them.
                    const showAlternatives = !!onOpenAlternatives
                        && !ingredient.custom && !ingredient.isCustom
                        && !ingredient.subrecipeId && !ingredient.nestedCostPriceId;
                    // The reference exclusions matter for pre-M35 analyses, which can still
                    // hold an INLINED subrecipe copy carrying both `custom` and `subrecipeId`;
                    // both cost twins price such a row as the whole subrecipe portion.
                    const showLink = !!onLinkCustomIngredient
                        && (ingredient.custom || ingredient.isCustom)
                        && !ingredient.subrecipeId && !ingredient.nestedCostPriceId && !ingredient.nestedSubrecipeId;

                    return (
                        <div
                            key={idx}
                            data-onboarding={practiceScope && isPracticeId(ingredient.articleId) ? `cost-dish-ingredient${(focusProductKey ? isFocusTarget : idx === 0) ? ' utilization-added-ingredient' : ''}` : undefined}
                            className={`rounded-lg border py-[11px] pl-3 pr-4 flex flex-col bg-backgroundSecondary dark:bg-backgroundSecondary-dark ${borderPrimary}`}
                        >
                            {/* Row 1: Product name + delete */}
                            <div className="flex justify-between items-start mb-2">
                                <div className="flex items-center gap-2 min-w-0 flex-1 flex-wrap">
                                    <p className={`text-[16px] leading-6 font-semibold tracking-[-0.16px] ${primaryText} min-w-0 truncate`}>
                                        {ingredient.productName}
                                    </p>
                                    <SourceRestaurantBadge restaurantName={(ingredient as any).sourceRestaurantName} />
                                </div>
                                <div className="flex items-center gap-2 flex-shrink-0 ml-2">
                                    {confirmingDeleteId === ingredient.id ? (
                                        <button type="button" onClick={() => onConfirmDelete(ingredient)} className="h-8 px-3 text-[14px] font-semibold bg-red-500 hover:bg-red-600 dark:bg-red-600 dark:hover:bg-red-700 text-white dark:text-white rounded-lg transition-colors">
                                            Remove
                                        </button>
                                    ) : (
                                        <button type="button" onClick={() => onDelete(ingredient)} className="p-1 hover:opacity-70 transition-opacity">
                                            <DeleteIcon />
                                        </button>
                                    )}
                                </div>
                            </div>

                            {/* Row 2: Supplier */}
                            {(platformDisplay.showLogo || platformDisplay.displayName) && (
                                <div className="flex items-center justify-between gap-3">
                                    <p className={`${greyTextColor} text-[14px] leading-[21px] font-normal tracking-[-0.14px]`}>Supplier</p>
                                    {platformDisplay.showLogo && platformDisplay.platformForLogo ? (
                                        <SupplierLogo
                                            platform={platformDisplay.platformForLogo}
                                            size="medium"
                                            alt={platformDisplay.displayName}
                                            className="object-contain"
                                        />
                                    ) : (
                                        <span className={`${secondaryText} text-[14px] leading-5 font-medium`}>{platformDisplay.displayName}</span>
                                    )}
                                </div>
                            )}

                            {/* Row 3: Unit Price */}
                            {unitPrice && (
                                <div className="flex items-center justify-between gap-3 mt-2">
                                    <p className={`${greyTextColor} text-[14px] leading-[21px] font-normal tracking-[-0.14px]`}>Unit Price</p>
                                    <p className={`${secondaryText} text-[14px] leading-5 font-medium`}>
                                        {eur(unitPrice.value)}/{unitPrice.unit}
                                    </p>
                                </div>
                            )}

                            {/* Row 4: Quantity (tag + reset appear when this dish overrides the shared value) */}
                            <div className="flex items-center justify-between gap-3 mt-2">
                                <div className="flex items-center gap-1.5">
                                    <p className={`${greyTextColor} text-[14px] leading-[21px] font-normal tracking-[-0.14px]`}>Quantity</p>
                                    {overriddenKeys?.has(ingredient.id) && <OverrideTag />}
                                </div>
                                <div className="flex items-center gap-1">
                                    {overriddenKeys?.has(ingredient.id) && onResetOverride && (
                                        <OverrideResetButton onClick={() => onResetOverride(ingredient.id)} />
                                    )}
                                    <input
                                        data-onboarding={practiceScope && isPracticeId(ingredient.articleId) && (focusProductKey ? isFocusTarget : idx === 0) ? "inventory-leakage-ing-qty" : undefined}
                                        ref={isFocusTarget ? focusInputRef : undefined}
                                        type="number" inputMode="decimal" min={0} step="any"
                                        value={currentQtyStr}
                                        onChange={(e) => {
                                            const value = e.target.value;
                                            if (value === '' || /^[0-9]*\.?[0-9]*$/.test(value)) onQuantityChange(ingredient.id, value);
                                        }}
                                        onBlur={() => onSaveEdit(ingredient.id, currentQtyStr, ingredient.quantityNeeded)}
                                        onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
                                        className="w-16 text-right bg-white dark:bg-background-dark border border-borderGrey dark:border-borderGrey-dark rounded px-2 py-0.5 text-[14px] leading-5 font-medium text-[#0C111D] dark:text-textPrimary-dark outline-none appearance-none [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                                    />
                                    <span className={`text-[14px] leading-5 font-medium ${secondaryText}`}>
                                        {ingredient.quantityInfo.stdUnit}
                                    </span>
                                </div>
                            </div>

                            {/* Row 4b: Waste % */}
                            {onWasteChange && onSaveWaste && (
                                <div className="flex items-center justify-between gap-3 mt-2">
                                    <p className={`${greyTextColor} text-[14px] leading-[21px] font-normal tracking-[-0.14px]`}>
                                        {t('costPrice.detail.wasteLabel')}
                                    </p>
                                    <div className="flex items-center gap-1">
                                        <input
                                            type="number" inputMode="decimal" min={0} max={100} step="0.5"
                                            value={wasteStr}
                                            placeholder={defaultWastePercentage.toString()}
                                            onChange={(e) => {
                                                const value = e.target.value;
                                                if (value === '' || /^[0-9]*\.?[0-9]*$/.test(value)) onWasteChange(ingredient.id, value);
                                            }}
                                            // Only persist when the user actually edited this row (an inline
                                            // entry exists) — avoids clobbering an existing override on a
                                            // focus/blur with no change.
                                            onBlur={() => { if (inlineWaste?.[ingredient.id] !== undefined) onSaveWaste(ingredient.id, inlineWaste[ingredient.id]); }}
                                            onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
                                            className="w-16 text-right bg-white dark:bg-background-dark border border-borderGrey dark:border-borderGrey-dark rounded px-2 py-0.5 text-[14px] leading-5 font-medium text-[#0C111D] dark:text-textPrimary-dark outline-none appearance-none [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                                        />
                                        <span className={`text-[14px] leading-5 font-medium ${secondaryText}`}>%</span>
                                    </div>
                                </div>
                            )}

                            {/* Row 5: Cost */}
                            <div className="flex justify-between items-center gap-2 mt-2">
                                <p className={`text-[14px] leading-[21px] font-normal tracking-[-0.14px] ${greyTextColor}`}>Cost</p>
                                <p className={`text-[14px] leading-5 font-medium ${textBlue}`}>{eur(ingredientCost)}</p>
                            </div>

                            {/* Row 6: footer strip. LEFT = Alternatives, order-history rows only
                                (never custom / subrecipe-ref / nested rows); cost is unaffected,
                                alternatives feed only the waste engine. RIGHT = Link, custom rows
                                only; it re-points the placeholder at real purchase data. */}
                            {(showAlternatives || showLink) && (
                                <div className="mt-3 pt-3 border-t border-borderGrey dark:border-borderGrey-dark flex flex-wrap items-center justify-between gap-x-3 gap-y-2">
                                    {requestContext && showAlternatives && !practiceScope && <AskCustomerButton
                                        disabled={requestDisabled}
                                        seed={{ ...requestContext, type: 'ingredient_dish_usage', mode: 'review_existing', productName: ingredient.productName }}
                                    />}
                                    {showAlternatives && onOpenAlternatives && (
                                        <button
                                            type="button"
                                            onClick={() => onOpenAlternatives(ingredient)}
                                            className={`flex items-center gap-1.5 text-[14px] leading-5 font-medium ${textBlue} hover:opacity-70 transition-opacity`}
                                        >
                                            <svg width="16" height="16" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                <path d="M5.83301 4.16663L2.49967 7.49996L5.83301 10.8333" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                                <path d="M2.49967 7.5H13.333C15.5421 7.5 17.333 9.29086 17.333 11.5C17.333 12.0523 17.2 12.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                                <path d="M14.1663 15.8334L17.4997 12.5001L14.1663 9.16675" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                                <path d="M17.5 12.5H6.66667C4.45753 12.5 2.66667 10.7091 2.66667 8.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                            </svg>
                                            {t('costPrice.alternatives.manageButton')}
                                            {ingredient.alternatives && ingredient.alternatives.length > 0 && (
                                                <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-blue-50 dark:bg-blue-900/40 text-[11px] font-semibold text-blue-600 dark:text-blue-300">
                                                    {ingredient.alternatives.length}
                                                </span>
                                            )}
                                        </button>
                                    )}
                                    {showLink && onLinkCustomIngredient && (
                                        <button
                                            type="button"
                                            onClick={() => onLinkCustomIngredient(ingredient)}
                                            className={`flex items-center gap-1.5 text-[14px] leading-5 font-medium ${greenTextColor} hover:opacity-70 transition-opacity`}
                                        >
                                            <Link2 className="w-4 h-4" />
                                            {t('costPrice.customLink.linkButton')}
                                        </button>
                                    )}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default IngredientsSection;
